<template>
  <div class="admin-panel-root">
    <div class="container py-5">
      <!-- Header -->
      <div class="admin-header d-flex justify-content-between align-items-center mb-5">
        <div class="header-title">
          <h1 class="fw-bold text-dark mb-1">User Management</h1>
          <p class="text-muted mb-0">Kelola pengguna dan hak akses sistem Sundava</p>
        </div>
        <div class="header-btns d-flex gap-2">
            <router-link to="/" class="btn btn-outline-secondary rounded-pill px-3 px-md-4">
                <vue-feather type="arrow-left" size="16" class="me-md-2"></vue-feather>
                <span class="d-none d-md-inline">Kembali ke Portal</span>
                <span class="d-inline d-md-none">Portal</span>
            </router-link>
            <button @click="showAddModal = true" class="btn btn-primary rounded-pill px-3 px-md-4 shadow-sm">
                <vue-feather type="user-plus" size="16" class="me-md-2"></vue-feather>
                <span class="d-none d-md-inline">Tambah User</span>
                <span class="d-inline d-md-none">Tambah</span>
            </button>
        </div>
      </div>

      <!-- Desktop Table View -->
      <div class="card border-0 shadow-premium rounded-4 overflow-hidden d-none d-md-block">
        <div class="table-responsive">
          <table class="table table-hover mb-0 align-middle">
            <thead class="bg-light">
              <tr>
                <th class="ps-4 py-3">User</th>
                <th class="py-3">Role</th>
                <th class="py-3">Modules Access</th>
                <th class="py-3">Status</th>
                <th class="text-end pe-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users" :key="user.id">
                <td class="ps-4 py-3">
                  <div class="d-flex align-items-center gap-3">
                    <div class="avatar-sm text-white rounded-circle d-flex align-items-center justify-content-center bg-gradient-success">
                      {{ (user.full_name || 'U').charAt(0).toUpperCase() }}
                    </div>
                    <div>
                      <div class="fw-bold text-dark">{{ user.full_name }}</div>
                    </div>
                  </div>
                </td>
                <td class="py-3">
                  <span class="badge rounded-pill" :class="user.role === 'admin' ? 'bg-danger-soft text-danger' : 'bg-success-soft text-success'">
                    {{ (user.role || 'user').toUpperCase() }}
                  </span>
                </td>
                <td class="py-3">
                  <div class="d-flex flex-wrap gap-1">
                    <span v-for="(actions, module) in user.permissions" :key="module" class="badge bg-light text-dark border small">
                      {{ module }}: {{ actions.length }}
                    </span>
                    <span v-if="!user.permissions || Object.keys(user.permissions).length === 0" class="text-muted small">No access</span>
                  </div>
                </td>
                <td class="py-3">
                  <span :class="user.is_active ? 'text-success' : 'text-danger'">
                    <i class="fas fa-circle small me-1"></i> {{ user.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="text-end pe-4 py-3">
                  <button @click="editPermissions(user)" class="btn btn-sm btn-light rounded-pill px-3 me-2">
                    Permissions
                  </button>
                  <button @click="toggleStatus(user)" class="btn btn-sm me-1" :class="user.is_active ? 'text-danger' : 'text-success'">
                    <vue-feather :type="user.is_active ? 'user-x' : 'user-check'" size="16"></vue-feather>
                  </button>
                  <button @click="confirmDelete(user)" class="btn btn-sm text-danger" title="Delete User">
                    <vue-feather type="trash-2" size="16"></vue-feather>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mobile Card List View -->
      <div class="mobile-user-list d-md-none">
        <div v-for="user in users" :key="'mob-' + user.id" class="user-mobile-card shadow-sm border mb-3 rounded-4 bg-white overflow-hidden">
           <!-- Card Top: Identity -->
           <div class="p-3 bg-light-soft border-bottom d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center gap-3">
                 <div class="avatar-sm mobile text-white rounded-circle d-flex align-items-center justify-content-center bg-gradient-success">
                    {{ (user.full_name || 'U').charAt(0).toUpperCase() }}
                 </div>
                 <div class="user-info">
                    <div class="fw-bold text-dark lh-1 mb-1">{{ user.full_name }}</div>
                 </div>
              </div>
              <span :class="user.is_active ? 'text-success' : 'text-danger'" class="smaller fw-bold">
                 <i class="fas fa-circle smallest me-1"></i> {{ user.is_active ? 'Active' : 'Inactive' }}
              </span>
           </div>

           <!-- Card Middle: Key Meta -->
           <div class="p-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                 <span class="text-muted smallest text-uppercase fw-bold">User Role</span>
                 <span class="badge rounded-pill" :class="user.role === 'admin' ? 'bg-danger-soft text-danger' : 'bg-success-soft text-success'">
                    {{ (user.role || 'user').toUpperCase() }}
                 </span>
              </div>
              <div class="mobile-perms-row d-flex flex-wrap gap-1">
                 <span v-for="(actions, module) in user.permissions" :key="'m-' + module" class="badge bg-light text-dark border smallest">
                    {{ module }}: {{ actions.length }}
                 </span>
                 <span v-if="!user.permissions || Object.keys(user.permissions).length === 0" class="text-muted smallest">No modules access</span>
              </div>
           </div>

           <!-- Card Bottom: Actions -->
           <div class="bg-light p-2 d-flex gap-2 border-top">
              <button @click="editPermissions(user)" class="btn btn-white btn-sm flex-fill rounded-pill py-2 shadow-sm border border-light-dark">
                 <vue-feather type="shield" size="14" class="me-1"></vue-feather>
                 Permissions
              </button>
              <button @click="toggleStatus(user)" class="btn btn-sm rounded-pill btn-action-square rounded-circle shadow-sm" :class="user.is_active ? 'btn-outline-danger bg-white' : 'btn-outline-success bg-white'">
                 <vue-feather :type="user.is_active ? 'user-x' : 'user-check'" size="16"></vue-feather>
              </button>
              <button @click="confirmDelete(user)" class="btn btn-white text-danger btn-sm rounded-circle btn-action-square shadow-sm border-danger-soft">
                 <vue-feather type="trash-2" size="16"></vue-feather>
              </button>
           </div>
        </div>
      </div>
    </div>

    <!-- Modals (Add User) -->
    <div v-if="showAddModal" class="modal-overlay">
      <div class="modal-content-custom shadow-lg">
        <h4 class="fw-bold mb-4">Tambah User Baru</h4>
        <form @submit.prevent="saveUser">
          <div class="mb-3">
            <label class="form-label small fw-bold">Full Name</label>
            <input v-model="newUser.full_name" type="text" class="form-control" required>
          </div>
          <div class="mb-3">
            <label class="form-label small fw-bold">Username</label>
            <input v-model="newUser.username" type="text" class="form-control" required>
          </div>
          <div class="mb-3">
            <label class="form-label small fw-bold">Email</label>
            <input v-model="newUser.email" type="email" class="form-control" required>
          </div>
          <div class="mb-4">
            <label class="form-label small fw-bold">Role</label>
            <select v-model="newUser.role" class="form-select">
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div class="d-flex gap-2 justify-content-end">
            <button type="button" @click="showAddModal = false" class="btn btn-light rounded-pill px-4">Cancel</button>
            <button type="submit" class="btn btn-primary rounded-pill px-4">Create User</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Permissions Modal -->
    <div v-if="showPermsModal" class="modal-overlay">
      <div class="modal-content-custom wide shadow-lg modal-flex-container">
        <div class="modal-header-fixed d-flex justify-content-between align-items-center mb-4">
          <div class="pe-3">
             <h4 class="fw-bold mb-0 text-truncate" style="max-width: 220px;">Perms: {{ selectedUser.full_name }}</h4>
          </div>
          <div class="d-flex align-items-center gap-2">
             <label class="small fw-bold text-muted mb-0">ROLE:</label>
             <select v-model="editingRole" class="form-select form-select-sm rounded-pill px-3" style="width: auto;">
                <option value="user">USER</option>
                <option value="admin">ADMIN</option>
             </select>
          </div>
        </div>
        
        <!-- Scrollable Body Area -->
        <div class="modal-body-scrollable">
          <div class="app-permission-section mb-0 p-3 p-md-4 border rounded-4 bg-white shadow-sm">
             <div class="d-flex align-items-center gap-2 mb-4">
                <div class="p-2 bg-success text-white rounded-3">
                   <vue-feather type="layers" size="18"></vue-feather>
                </div>
                <h5 class="fw-bold mb-0">SSP App (SSP ADMIN)</h5>
             </div>
  
             <div class="row g-3 g-md-4">
                <div v-for="module in modules" :key="module.id" class="col-md-6">
                   <div class="module-card p-3 border rounded-3 bg-light-soft">
                      <div class="fw-bold mb-2 text-dark text-uppercase small tracking-wider">{{ module.name }}</div>
                      <div class="d-flex gap-3 flex-wrap">
                         <div v-for="action in ['view', 'create', 'edit', 'delete']" :key="action" class="form-check">
                            <input 
                             type="checkbox" 
                             :id="module.id + action" 
                             class="form-check-input"
                             :checked="hasPermission('ssp', module.id, action)"
                             @change="togglePermission('ssp', module.id, action)"
                            >
                            <label :for="module.id + action" class="form-check-label small text-capitalize">{{ action }}</label>
                         </div>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </div>
 
        <!-- Persistent Footer -->
        <div class="modal-footer-fixed mt-4 d-flex gap-2 justify-content-end pt-3 border-top">
          <button @click="showPermsModal = false" class="btn btn-light rounded-pill px-4">Cancel</button>
          <button @click="savePermissions" class="btn btn-primary rounded-pill px-4">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import adminUserService from "@/services/adminUserService";
import Swal from 'sweetalert2';

export default {
  setup() {
    const users = ref([]);
    const showAddModal = ref(false);
    const showPermsModal = ref(false);
    const selectedUser = ref(null);
    const editingPermissions = ref({});
    const editingRole = ref('user');

    const newUser = ref({
      full_name: '',
      username: '',
      email: '',
      role: 'user'
    });

    const modules = [
      { id: 'products', name: 'Products Data' },
      { id: 'members', name: 'SSP Kiosk Data' },
      { id: 'orders', name: 'Orders Data' },
      { id: 'pendampings', name: 'Pendamping Data' },
      { id: 'sources', name: 'Product Sources' }
    ];

    const loadUsers = async () => {
      try {
        users.value = await adminUserService.getUsers();
      } catch (e) {
        console.error(e);
      }
    };

    const saveUser = async () => {
      try {
        await adminUserService.createUser(newUser.value);
        showAddModal.value = false;
        loadUsers();
        Swal.fire('Success', 'User created with default password: sundava2026', 'success');
        newUser.value = { full_name: '', username: '', email: '', role: 'user' };
      } catch (e) {
        Swal.fire('Error', e.response?.data?.detail || 'Failed to create user', 'error');
      }
    };

    const editPermissions = (user) => {
      selectedUser.value = user;
      // Strict initialization: only take known app keys to keep JSON clean
      const rawPerms = user.permissions || {};
      editingPermissions.value = {
        ssp: rawPerms.ssp || {}
      };
      editingRole.value = user.role || 'user';
      showPermsModal.value = true;
      document.body.style.overflow = 'hidden';
    };

    const hasPermission = (appId, moduleId, action) => {
      return editingPermissions.value[appId]?.[moduleId]?.includes(action);
    };

    const togglePermission = (appId, moduleId, action) => {
      if (!editingPermissions.value[appId]) {
        editingPermissions.value[appId] = {};
      }
      if (!editingPermissions.value[appId][moduleId]) {
        editingPermissions.value[appId][moduleId] = [];
      }
      const index = editingPermissions.value[appId][moduleId].indexOf(action);
      if (index > -1) {
        editingPermissions.value[appId][moduleId].splice(index, 1);
      } else {
        editingPermissions.value[appId][moduleId].push(action);
      }
    };

    const savePermissions = async () => {
      try {
        await adminUserService.updatePermissions(selectedUser.value.id, {
          permissions: editingPermissions.value,
          role: editingRole.value
        });
        showPermsModal.value = false;
        document.body.style.overflow = '';
        loadUsers();
        Swal.fire('Saved', 'Permissions updated successfully', 'success');
      } catch (e) {
        Swal.fire('Error', 'Failed to update permissions', 'error');
      }
    };

    const toggleStatus = async (user) => {
        try {
            await adminUserService.updatePermissions(user.id, {
                is_active: !user.is_active
            });
            loadUsers();
        } catch (e) {
            Swal.fire('Error', 'Failed to update user status', 'error');
        }
    };

    const confirmDelete = (user) => {
        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        if (currentUser && currentUser.id === user.id) {
            return Swal.fire('Error', 'You cannot delete your own admin account!', 'error');
        }

        Swal.fire({
            title: 'Hapus User?',
            text: `Apakah Anda yakin ingin menghapus user ${user.full_name}? Data yang dihapus tidak bisa dikembalikan.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
            confirmButtonColor: '#d33',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await adminUserService.deleteUser(user.id);
                    Swal.fire('Deleted!', 'User has been removed.', 'success');
                    loadUsers();
                } catch (e) {
                    Swal.fire('Error', 'Failed to delete user', 'error');
                }
            }
        });
    };

    onMounted(loadUsers);

    return {
      users, modules, showAddModal, showPermsModal, newUser, selectedUser, editingRole,
      saveUser, editPermissions, hasPermission, togglePermission, savePermissions, toggleStatus, confirmDelete
    };
  }
}
</script>

<style scoped>
.admin-panel-root {
  min-height: 100vh;
  background-color: #f8fafc;
}
.shadow-premium {
  box-shadow: 0 10px 40px rgba(0,0,0,0.04);
}
.bg-gradient-success {
  background: linear-gradient(135deg, #059468, #10b981);
}
.avatar-sm {
  width: 40px;
  height: 40px;
  font-weight: 700;
}
.bg-success-soft { background-color: #ecfdf5; }
.bg-danger-soft { background-color: #fef2f2; }
.bg-light-soft { background-color: #fcfcfd; }

.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content-custom {
  background: white;
  padding: 30px;
  border-radius: 24px;
  width: 90%;
  max-width: 500px;
}
.modal-content-custom.wide {
  max-width: 800px;
}
.modal-flex-container {
  display: flex;
  flex-direction: column;
  max-height: 90vh; /* Consistent height */
}
.modal-body-scrollable {
  flex: 1;
  overflow-y: auto;
  padding-right: 5px; /* space for scrollbar */
}
.modal-header-fixed { flex-shrink: 0; }
.modal-footer-fixed { flex-shrink: 0; }
.btn-primary {
  background-color: #059468;
  border: none;
}
.btn-primary:hover {
  background-color: #047857;
}
.form-check-input:checked {
  background-color: #059468;
  border-color: #059468;
}

/* User Card Styles */
.user-mobile-card {
  transition: transform 0.1s ease;
}
.user-mobile-card:active {
  transform: scale(0.98);
}
.smallest { font-size: 11px; }
.smaller { font-size: 12px; }
.lh-1 { line-height: 1; }
.avatar-sm.mobile {
  width: 38px;
  height: 38px;
  font-size: 14px;
}
.btn-white {
  background: white;
  border: 1px solid #dee2e6;
  color: #475569;
  font-weight: 700;
}
.border-danger-soft {
  border-color: #fee2e2;
  background-color: #fef2f2;
}
.btn-action-square {
   width: 38px;
   height: 38px;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 0;
}

/* Responsive Enhancements */
@media (max-width: 767.98px) {
  .container {
    padding-top: 1.5rem !important;
    padding-bottom: 2rem !important;
  }
  
  .admin-header {
    flex-direction: column !important;
    align-items: center !important;
    text-align: center;
    gap: 15px;
    margin-bottom: 30px !important;
  }
  
  .header-btns {
    width: 100%;
    justify-content: center;
    padding: 0 10px;
  }

  .header-btns .btn {
    flex: 1;
    font-size: 14px;
    padding: 12px !important;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .header-title h1 {
    font-size: 28px;
  }
  
  .header-title p {
    font-size: 14px;
  }

  .modal-content-custom {
    width: 95%;
    padding: 20px;
    max-height: 85vh;
    border-radius: 20px;
  }
  
  .modal-content-custom h4 {
    font-size: 20px;
  }
  
  .module-card {
    padding: 12px !important;
  }

  .app-permission-section {
    padding: 15px !important;
  }
}
</style>
